import clsx from 'clsx';
import { ArrowLeft, ListChecks, MessagesSquare, Plus, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useProjects, useTasks, useThreads } from '@/api';
import type { Thread as ThreadT } from '@/api/types';
import { formatWhen } from '@/lib/format';
import { NewDiscussionDialog } from './NewDiscussionDialog';
import { Thread } from './Thread';
import { Empty } from './ui';
import { QueryPending } from '@/components/QueryState';

type Filter = 'all' | 'unread' | 'reply' | 'project';

/**
 * Inbox of conversations with the open one beside it. Used inside a project
 * (its general thread plus one per task) and as the global message inbox.
 */
export function ThreadsView({ projectId, selectedId, base }: { projectId?: string; selectedId?: string; base: string }) {
  const threadsQ = useThreads(projectId);
  const threads = threadsQ.data;
  const { data: projects } = useProjects();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('all');
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const { data: tasks } = useTasks(projectId);
  const taskKey = (id?: string) => tasks?.find((x) => x.id === id)?.key;

  const filters: { value: Filter; label: string; test: (t: ThreadT) => boolean }[] = [
    { value: 'all', label: 'Все', test: () => true },
    { value: 'unread', label: 'Непрочитанные', test: (t) => t.unread > 0 },
    { value: 'reply', label: 'Ждут вашего ответа', test: (t) => t.awaitingClient },
    { value: 'project', label: 'Общие по проектам', test: (t) => !t.taskId },
  ];

  const list = useMemo(() => {
    if (!threads) return [];
    const test = filters.find((f) => f.value === filter)!.test;
    const needle = q.trim().toLowerCase();
    return threads.filter((t) => test(t) && (!needle || `${t.title} ${t.lastMessage}`.toLowerCase().includes(needle)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threads, filter, q]);

  const selected = threads?.find((t) => t.id === selectedId);
  const projectTitle = (id: string) => projects?.find((p) => p.id === id)?.title ?? '';
  const pinned = !projectId ? [] : list.filter((t) => !t.taskId);
  const rest = !projectId ? list : list.filter((t) => t.taskId);

  const item = (t: ThreadT) => (
    <li key={t.id}>
      <Link
        to={`${base}/${t.id}`}
        aria-current={t.id === selectedId ? 'true' : undefined}
        className={clsx('block px-4 py-3 transition-colors', t.id === selectedId ? 'bg-sunken' : 'hover:bg-panel')}
      >
        <div className="flex items-center gap-2">
          {t.taskId ? <ListChecks className="size-3.5 shrink-0 text-faint" /> : <MessagesSquare className="size-3.5 shrink-0 text-faint" />}
          <span className={clsx('min-w-0 flex-1 truncate text-sm', t.unread ? 'font-semibold' : 'font-medium')}>{t.title}</span>
          {t.taskId && taskKey(t.taskId) && <span className="num shrink-0 rounded-sm bg-sunken px-1.5 font-mono text-[11px] leading-4 text-muted">{taskKey(t.taskId)}</span>}
          <time className="shrink-0 text-xs text-faint">{formatWhen(t.lastMessageAt)}</time>
        </div>
        {!projectId && <div className="mt-0.5 truncate pl-5 text-xs text-muted">{projectTitle(t.projectId)}</div>}
        <div className="mt-1 flex items-center gap-2 pl-5">
          <span className="line-clamp-1 flex-1 text-sm text-muted">
            {t.lastAuthor && <span className="text-ink/80">{t.lastAuthor.side === 'client' ? 'Вы' : t.lastAuthor.name.split(' ')[0]}: </span>}
            {t.lastMessage}
          </span>
          {t.unread > 0 ? (
            <span className="num shrink-0 rounded-full bg-ink px-1.5 text-[10px] font-semibold leading-4 text-inverse">{t.unread}</span>
          ) : t.awaitingClient ? (
            <span className="shrink-0 text-xs text-waiting">ждут ответа</span>
          ) : (
            <span className="num shrink-0 text-xs text-faint">{t.total}</span>
          )}
        </div>
      </Link>
    </li>
  );

  return (
    <div className="panel grid grid-cols-1 h-[calc(100vh-200px)] min-h-[560px] overflow-hidden lg:grid-cols-[360px_1fr]">
      <aside className={clsx('flex min-h-0 flex-col border-line lg:border-r', selectedId && 'hidden lg:flex')}>
        <div className="space-y-2 border-b border-line p-3">
          {projectId && (
            <button className="btn-primary w-full" onClick={() => setCreating(true)}>
              <Plus className="size-4" /> Новое обсуждение
            </button>
          )}
          <label className="relative block">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-faint" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Найти в переписке" className="field h-8 py-0 pl-8 text-sm" />
          </label>
          <div className="flex gap-1.5 overflow-x-auto pb-0.5">
            {filters
              .filter((f) => !(projectId && f.value === 'project'))
              .map((f) => (
                <button key={f.value} className="chip h-6 px-2.5 text-xs" aria-pressed={filter === f.value} onClick={() => setFilter(f.value)}>
                  {f.label}
                  <span className="opacity-60">{threads?.filter(f.test).length}</span>
                </button>
              ))}
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {!threads ? (
            <div className="p-4">
              <QueryPending query={threadsQ} compact />
            </div>
          ) : list.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted">Ничего не нашли.</p>
          ) : (
            <>
              {pinned.length > 0 && <ul className="divide-y divide-line border-b border-line">{pinned.map(item)}</ul>}
              {projectId && rest.length > 0 && <div className="px-4 pb-1 pt-3 text-xs text-faint">По задачам</div>}
              <ul className="divide-y divide-line">{rest.map(item)}</ul>
            </>
          )}
        </div>
      </aside>

      <section className={clsx('flex min-h-0 flex-col', !selectedId && 'hidden lg:flex')}>
        {selected ? (
          <>
            <div className="flex items-center gap-3 border-b border-line px-5 py-3">
              <button onClick={() => navigate(base)} className="btn-ghost size-8 px-0 lg:hidden" aria-label="К списку">
                <ArrowLeft className="size-4" />
              </button>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{selected.title}</div>
                <div className="text-xs text-muted">
                  {projectTitle(selected.projectId)}, {selected.total} сообщений
                </div>
              </div>
              <Link className="btn-secondary shrink-0" to={selected.taskId ? `/p/${selected.projectId}/tasks/${selected.taskId}` : `/p/${selected.projectId}`}>
                {selected.taskId ? 'Открыть задачу' : 'Открыть проект'}
              </Link>
            </div>
            <Thread key={selected.id} threadId={selected.id} projectId={selected.projectId} unread={selected.unread} className="flex-1" />
          </>
        ) : (
          <div className="grid flex-1 place-items-center p-10">
            <Empty title="Выберите переписку">Слева — общий разговор по проекту и отдельные обсуждения задач. Непрочитанные выделены.</Empty>
          </div>
        )}
      </section>
      {projectId && (
        <NewDiscussionDialog open={creating} onClose={() => setCreating(false)} projectId={projectId} tasks={tasks ?? []} onCreated={(t) => navigate(`${base}/${t.id}`)} />
      )}
    </div>
  );
}
